'use client'

import { TrustBanner } from '@/components/shared/trust-banner'
import { PersistedSingleQuiz } from '@/components/shared/Quiz/quiz'
import { SectionDescription, SectionHeader, SectionTitle } from '@/components/ui/section'
import { useTranslations } from 'next-intl'

type MainLandingScreenProps = {
  onNext: (value: string) => void
  posthogProperties?: Record<string, string | number>
}

export function MainLandingScreen({ onNext, posthogProperties }: MainLandingScreenProps) {
  const tLanding = useTranslations('landing')
  const tFunnel = useTranslations('funnel')

  return (
    <main className="roast-hero-halo flex min-h-dvh flex-col bg-background">
      <TrustBanner
        placement="static"
        ratingLabel={tLanding('trust_banner.rating')}
        profilesLabel={tLanding('trust_banner.profiles_improved')}
      />

      <section className="mx-auto flex w-full max-w-2xl flex-1 flex-col px-5 pb-9 pt-7 md:pt-10">
        <SectionHeader className="mt-0">
          <SectionTitle
            as="h1"
            className="mx-auto max-w-[34rem] text-center text-[2.55rem] leading-[0.9] text-balance text-foreground sm:text-5xl md:text-[3.35rem]"
          >
            {tLanding('main.title')}
          </SectionTitle>
          <SectionDescription className="mx-auto mt-3 max-w-md text-center text-base leading-relaxed text-muted-foreground md:text-lg">
            {tLanding('main.description')}
          </SectionDescription>
        </SectionHeader>

        <div className="mx-auto mt-7 w-full max-w-xl border-y border-white/10 py-4">
          <p className="heading-display text-left text-sm font-medium text-primary">
            {tLanding('main.report_includes_label')}
          </p>
          <ol className="mt-3 flex flex-col gap-2.5">
            {(['report_item_1', 'report_item_2', 'report_item_3'] as const).map((key, i) => (
              <li
                key={key}
                className="flex items-start gap-3 text-sm leading-snug text-muted-foreground md:text-base"
              >
                <span
                  className="font-heading mt-0.5 flex h-5 min-w-5 shrink-0 items-center justify-center bg-primary/15 text-sm font-extrabold text-primary"
                  aria-hidden
                >
                  {i + 1}
                </span>
                <span>
                  {tLanding.rich(`main.${key}`, {
                    b: (chunks) => (
                      <strong className="font-semibold text-foreground">{chunks}</strong>
                    ),
                  })}
                </span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mx-auto mt-8 w-full max-w-xl">
          <h2 className="heading-display text-center text-[1.75rem] leading-none font-extrabold text-foreground md:text-3xl">
            {tLanding('main.gender_heading')}
          </h2>
          <div className="mt-4 w-full">
            <PersistedSingleQuiz
              options={[
                { icon: '👨', label: tFunnel('gender.options.male'), value: 'male' },
                { icon: '👩', label: tFunnel('gender.options.female'), value: 'female' },
              ]}
              storeKey="gender"
              variant="row"
              className="gap-2.5"
              posthogProperties={posthogProperties}
              onNext={onNext}
            />
          </div>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            {tLanding('main.footer_line')}
          </p>
        </div>
      </section>
    </main>
  )
}
